import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Calendar,
  CreditCard,
  Building2,
  Truck,
  Banknote,
  TrendingUp,
  Receipt,
  History,
} from 'lucide-react';
import {
  getOrderLedgerMaster,
  OrderLedgerMasterResponse,
} from '../../services/MasterAdmin/accountantService';
import { fetchClinicAppointmentInvoiceBlob } from '../../services/MasterAdmin/doctorClinicService';

/** Ledger-only sections for grievance resolve flow (no vendor shop / transport summaries). */
const GRIEVANCE_LEDGER_SECTIONS = new Set([
  'payment',
  'customerAccount',
  'settlementLedgers',
  'refunds',
  'razorpayAndBank',
  'gatewayCharges',
  'auditTrail',
]);

const SERVICE_TYPE_SECTIONS: Record<string, string[]> = {
  AMBULANCE: [
    'payment',
    'customerAccount',
    'vendorTransportation',
    'settlementLedgers',
    'refunds',
    'razorpayAndBank',
    'gatewayCharges',
    'auditTrail',
  ],
  MEDICINE: ['payment', 'customerAccount', 'vendorMedical', 'settlementLedgers', 'refunds', 'commissionSummary'],
  HOSPITAL: ['payment', 'customerAccount', 'vendorMedical', 'settlementLedgers', 'refunds'],
  DOCTOR_INSTANT: ['payment', 'customerAccount', 'vendorMedical', 'settlementLedgers', 'refunds', 'commissionSummary'],
  DOCTOR_SCHEDULED: ['payment', 'customerAccount', 'vendorMedical', 'settlementLedgers', 'refunds', 'commissionSummary'],
  LAB_TEST: ['payment', 'customerAccount', 'vendorMedical', 'settlementLedgers', 'refunds'],
  BLOOD_BANK: ['payment', 'customerAccount', 'vendorMedical', 'settlementLedgers', 'refunds'],
  PRODUCT: ['payment', 'customerAccount', 'vendorMedical', 'settlementLedgers', 'refunds'],
};

const LEDGER_TYPE_SECTIONS: Record<string, string[]> = {
  customer: ['payment', 'customerAccount', 'refunds', 'settlementLedgers'],
  vendor: ['payment', 'vendorMedical', 'settlementLedgers'],
  'delivery-partner': ['payment', 'vendorTransportation', 'settlementLedgers'],
  razorpay: ['payment', 'razorpayAndBank', 'gatewayCharges'],
  bank: ['payment', 'razorpayAndBank', 'gatewayCharges'],
  commission: ['payment', 'commissionSummary', 'settlementLedgers'],
  'profit-loss': ['payment', 'profitAndLoss', 'settlementLedgers', 'gatewayCharges', 'refunds'],
  gst: ['payment', 'commissionSummary'],
  payouts: ['payment', 'payouts'],
};

type LedgerTableRow = {
  label: string;
  value: string | React.ReactNode;
  align: 'left' | 'right';
  highlight?: boolean;
};

const isDoctorServiceType = (serviceType: string | null | undefined) =>
  serviceType === 'DOCTOR_INSTANT' || serviceType === 'DOCTOR_SCHEDULED';

function buildCustomerAccountRows(
  ledgerData: OrderLedgerMasterResponse,
  serviceType: string | null,
  grievanceView: boolean,
  formatCurrency: (amount: number | null | undefined) => string
): LedgerTableRow[] {
  const ca = ledgerData.customerAccount;

  if (isDoctorServiceType(serviceType)) {
    const rows: LedgerTableRow[] = [
      {
        label: 'Consultation Fee',
        value: formatCurrency(ca.consultationFee ?? 0),
        align: 'right',
      },
    ];
    if (Number(ca.discountAmount || 0) > 0) {
      rows.push({
        label: 'Discount',
        value: `-${formatCurrency(ca.discountAmount)}`,
        align: 'right',
      });
    }
    rows.push(
      {
        label: 'Paid Amount',
        value: formatCurrency(ca.totalPaid),
        align: 'right',
        highlight: true,
      },
      {
        label: 'Refunded Amount',
        value: formatCurrency(ca.refundedAmount),
        align: 'right',
      }
    );
    return rows;
  }

  const rows: LedgerTableRow[] = [];
  const hideMedicines =
    grievanceView && (serviceType === 'AMBULANCE' || !Number(ca.medicinesAmount));
  if (!hideMedicines) {
    rows.push({
      label: 'Medicines Amount',
      value: formatCurrency(ca.medicinesAmount),
      align: 'right',
    });
  }
  if (!grievanceView || serviceType === 'AMBULANCE' || Number(ca.transportAmount)) {
    rows.push({
      label: grievanceView && serviceType === 'AMBULANCE' ? 'Booking amount' : 'Transport Amount',
      value: formatCurrency(ca.transportAmount),
      align: 'right',
    });
  }
  rows.push(
    {
      label: 'Total Paid',
      value: formatCurrency(ca.totalPaid),
      align: 'right',
      highlight: true,
    },
    {
      label: 'Refunded Amount',
      value: formatCurrency(ca.refundedAmount),
      align: 'right',
    }
  );
  return rows;
}

function buildVendorMedicalRows(
  ledgerData: OrderLedgerMasterResponse,
  serviceType: string | null,
  formatCurrency: (amount: number | null | undefined) => string,
  getStatusBadge: (status: string | null | undefined) => React.ReactNode
): LedgerTableRow[] {
  const vm = ledgerData.vendorMedical;

  if (isDoctorServiceType(serviceType)) {
    const rows: LedgerTableRow[] = [
      {
        label: 'Consultation Fee',
        value: formatCurrency(vm.grossAmount),
        align: 'right',
      },
    ];
    if (Number(vm.discount || 0) > 0) {
      rows.push({
        label: 'Discount',
        value: `-${formatCurrency(vm.discount)}`,
        align: 'right',
      });
    }
    rows.push(
      {
        label: 'Net Payable',
        value: formatCurrency(vm.netPayable),
        align: 'right',
        highlight: true,
      },
      {
        label: 'Settlement Status',
        value: getStatusBadge(vm.settlementStatus),
        align: 'left',
      }
    );
    return rows;
  }

  return [
    { label: 'Gross Amount', value: formatCurrency(vm.grossAmount), align: 'right' },
    { label: 'Discount', value: formatCurrency(vm.discount), align: 'right' },
    { label: 'Commission', value: formatCurrency(vm.commission), align: 'right' },
    { label: 'GST on Commission', value: formatCurrency(vm.gstOnCommission), align: 'right' },
    { label: 'Refund Impact', value: formatCurrency(vm.refundImpact), align: 'right' },
    { label: 'Net Payable', value: formatCurrency(vm.netPayable), align: 'right', highlight: true },
    { label: 'Settlement Status', value: getStatusBadge(vm.settlementStatus), align: 'left' },
  ];
}

const OrderLedgerPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const ledgerType = searchParams.get('ledgerType');
  const serviceType = searchParams.get('serviceType');
  const returnTo = searchParams.get('returnTo');
  const isGrievanceView = returnTo === 'grievances';
  const isMaContext = location.pathname.includes('/master-admin/operations/order-ledger');
  
  const [ledgerData, setLedgerData] = useState<OrderLedgerMasterResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const handleBack = useCallback(() => {
    if (returnTo === 'manual-refund-cases') {
      navigate('/master-admin/operations/manual-refund-cases');
      return;
    }
    if (returnTo === 'grievances') {
      navigate(-1);
      return;
    }
    navigate(-1);
  }, [navigate, returnTo]);

  const visibleSections = useMemo((): Set<string> | null => {
    if (isGrievanceView) return GRIEVANCE_LEDGER_SECTIONS;
    if (serviceType && SERVICE_TYPE_SECTIONS[serviceType]) {
      return new Set(SERVICE_TYPE_SECTIONS[serviceType]);
    }
    if (ledgerType && LEDGER_TYPE_SECTIONS[ledgerType]) {
      return new Set(LEDGER_TYPE_SECTIONS[ledgerType]);
    }
    return null;
  }, [isGrievanceView, serviceType, ledgerType]);

  const showSection = useCallback(
    (sectionKey: string) => {
      if (visibleSections) return visibleSections.has(sectionKey);
      switch (sectionKey) {
        case 'payment':
        case 'settlementLedgers':
        case 'gatewayCharges':
        case 'refunds':
          return true;
        case 'customerAccount':
          return !ledgerType || ledgerType === 'customer';
        case 'vendorMedical':
          return !ledgerType || ledgerType === 'vendor';
        case 'vendorTransportation':
          return !ledgerType || ledgerType === 'delivery-partner';
        case 'razorpayAndBank':
          return !ledgerType || ledgerType === 'razorpay' || ledgerType === 'bank';
        case 'commissionSummary':
          return !ledgerType || ledgerType === 'commission' || ledgerType === 'gst';
        case 'profitAndLoss':
          return !ledgerType || ledgerType === 'profit-loss';
        case 'payouts':
          return (!ledgerType || ledgerType === 'payouts') && (ledgerData?.payouts?.length ?? 0) > 0;
        case 'auditTrail':
          return Boolean(ledgerType);
        default:
          return false;
      }
    },
    [visibleSections, ledgerType, ledgerData?.payouts?.length]
  );

  const defaultExpandedSections = useMemo(() => {
    if (visibleSections) return new Set(visibleSections);
    return new Set([
      'payment',
      'customerAccount',
      'vendorMedical',
      'vendorTransportation',
      'razorpayAndBank',
      'commissionSummary',
      'profitAndLoss',
      'settlementLedgers',
      'gatewayCharges',
      'refunds',
      'auditTrail',
    ]);
  }, [visibleSections]);
  
  const [expandedSections, setExpandedSections] = useState<Set<string>>(defaultExpandedSections);

  const fetchLedgerData = useCallback(async () => {
    if (!orderId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await getOrderLedgerMaster(orderId);
      setLedgerData(response.data);
    } catch (err: any) {
      console.error('Error fetching ledger data:', err);
      setError(err.response?.data?.message || 'Failed to fetch ledger data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId) {
      fetchLedgerData();
    }
  }, [orderId, fetchLedgerData]);

  useEffect(() => {
    // Reset expanded sections when ledger type changes
    setExpandedSections(defaultExpandedSections);
  }, [defaultExpandedSections]);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '\u20b90.00';
    return `\u20b9${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadge = (status: string | null | undefined) => {
    if (!status) return null;
    
    const statusLower = status.toLowerCase();
    let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    
    if (statusLower === 'paid' || statusLower === 'settled' || statusLower === 'completed') {
      bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    } else if (statusLower === 'pending' || statusLower === 'created') {
      bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    } else if (statusLower === 'failed' || statusLower === 'on_hold') {
      bgColor = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    }
    
    return (
      <span className={`px-2 py-1 rounded-md text-xs font-medium ${bgColor}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const openMasterAdminInvoice = async () => {
    if (!orderId) return;
    try {
      const invoiceBlob = await fetchClinicAppointmentInvoiceBlob(orderId, 'masteradmin');
      const blobUrl = URL.createObjectURL(invoiceBlob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error('Failed to open master admin clinic invoice:', err);
      window.alert('Failed to open master admin invoice. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <div>
            <h3 className="font-semibold text-red-800 dark:text-red-400">Error</h3>
            <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!ledgerData) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-400">No ledger data found for this order.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
          <button
            type="button"
            onClick={handleBack}
            className="shrink-0 rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="break-all text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">
              Order Ledger: {orderId}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {isGrievanceView
                ? 'Ledger entries for grievance resolution (payment, settlements, refunds, gateway)'
                : isMaContext
                  ? serviceType
                    ? `Finance breakdown (${serviceType.replace(/_/g, ' ').toLowerCase()})`
                    : 'Finance breakdown for this booking'
                  : 'Complete financial breakdown for this order'}
            </p>
          </div>
        </div>
        {isDoctorServiceType(serviceType) &&
        String(ledgerData?.payment.paymentStatus || '').toLowerCase() === 'paid' ? (
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => void openMasterAdminInvoice()}
              className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-900/20 dark:text-violet-300"
            >
              Master Admin Invoice
            </button>
          </div>
        ) : null}
      </div>

      {/* Order Summary Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Payment Status</p>
            <div className="mt-1">
              {getStatusBadge(ledgerData.payment.paymentStatus)}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Paid</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
              {formatCurrency(ledgerData.customerAccount.totalPaid)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Payment Date</p>
            <p className="text-sm text-gray-900 dark:text-white mt-1">
              {formatDate(ledgerData.payment.paymentDate)}
            </p>
          </div>
        </div>
      </div>

      {/* Ledger Sections */}
      <div className="space-y-4">

        {showSection('payment') && (
        <LedgerSection
          title="Payment"
          icon={<CreditCard className="w-5 h-5" />}
          isExpanded={isGrievanceView || expandedSections.has('payment')}
          onToggle={isGrievanceView ? undefined : () => toggleSection('payment')}
        >
          <LedgerTable
            showHeader={isGrievanceView}
            rows={[
              { label: 'Paid Amount', value: formatCurrency(ledgerData.payment.paidAmount), align: 'right' },
              { label: 'Payment Method', value: ledgerData.payment.paymentMethod || 'N/A', align: 'left' },
              { label: 'Payment Status', value: getStatusBadge(ledgerData.payment.paymentStatus), align: 'left' },
              { label: 'Payment Date', value: formatDate(ledgerData.payment.paymentDate), align: 'left' },
            ]}
          />
        </LedgerSection>
        )}

        {showSection('customerAccount') && (
          <LedgerSection
            title={isDoctorServiceType(serviceType) ? 'User Account' : 'Customer Account'}
            icon={<Receipt className="w-5 h-5" />}
            isExpanded={isGrievanceView || expandedSections.has('customerAccount')}
            onToggle={isGrievanceView ? undefined : () => toggleSection('customerAccount')}
          >
            <LedgerTable
              rows={buildCustomerAccountRows(ledgerData, serviceType, isGrievanceView, formatCurrency)}
              showHeader={isGrievanceView}
            />
          </LedgerSection>
        )}

        {showSection('vendorMedical') && (
          <LedgerSection
            title={isDoctorServiceType(serviceType) ? 'Doctor Settlement' : 'Vendor Medical Shop'}
            icon={<Building2 className="w-5 h-5" />}
            isExpanded={expandedSections.has('vendorMedical')}
            onToggle={() => toggleSection('vendorMedical')}
          >
            <LedgerTable
              rows={buildVendorMedicalRows(
                ledgerData,
                serviceType,
                formatCurrency,
                getStatusBadge
              )}
            />
          </LedgerSection>
        )}

        {showSection('vendorTransportation') && (
          <LedgerSection
            title="Vendor Transportation"
            icon={<Truck className="w-5 h-5" />}
            isExpanded={expandedSections.has('vendorTransportation')}
            onToggle={() => toggleSection('vendorTransportation')}
          >
            <LedgerTable
              rows={[
                { label: 'Transport Charge', value: formatCurrency(ledgerData.vendorTransportation.transportCharge), align: 'right' },
                { label: 'Delivery Partner Pay', value: formatCurrency(ledgerData.vendorTransportation.deliveryPartnerPay), align: 'right', highlight: true },
                { label: 'Settlement Status', value: getStatusBadge(ledgerData.vendorTransportation.settlementStatus), align: 'left' },
              ]}
            />
          </LedgerSection>
        )}

        {showSection('razorpayAndBank') && (
          <LedgerSection
            title="Razorpay / Bank"
            icon={<Banknote className="w-5 h-5" />}
            isExpanded={isGrievanceView || expandedSections.has('razorpayAndBank')}
            onToggle={isGrievanceView ? undefined : () => toggleSection('razorpayAndBank')}
          >
            <LedgerTable
              showHeader={isGrievanceView}
              rows={[
                { label: 'Gateway Charges', value: formatCurrency(ledgerData.razorpayAndBank.gatewayCharges), align: 'right' },
                { label: 'GST on Gateway', value: formatCurrency(ledgerData.razorpayAndBank.gstOnGateway), align: 'right' },
                { label: 'Total Gateway Cost', value: formatCurrency(ledgerData.razorpayAndBank.totalGatewayCost), align: 'right', highlight: true },
                { label: 'Payout to Bank', value: formatCurrency(ledgerData.razorpayAndBank.payoutToBank), align: 'right' },
              ]}
            />
          </LedgerSection>
        )}

        {showSection('commissionSummary') && (
          <LedgerSection
            title={ledgerType === 'gst' ? 'GST Ledger' : 'Commission'}
            icon={<TrendingUp className="w-5 h-5" />}
            isExpanded={expandedSections.has('commissionSummary')}
            onToggle={() => toggleSection('commissionSummary')}
          >
            <LedgerTable
              rows={[
                { label: 'Commission Earned', value: formatCurrency(ledgerData.commissionSummary.commissionEarned), align: 'right' },
                { label: 'GST Payable', value: formatCurrency(ledgerData.commissionSummary.gstPayable), align: 'right', highlight: ledgerType === 'gst' },
                { label: 'Total Commission with GST', value: formatCurrency(ledgerData.commissionSummary.totalCommissionWithGst), align: 'right', highlight: ledgerType !== 'gst' },
              ]}
            />
          </LedgerSection>
        )}

        {showSection('profitAndLoss') && (
          <LedgerSection
            title="Profit & Loss"
            icon={<TrendingUp className="w-5 h-5" />}
            isExpanded={expandedSections.has('profitAndLoss')}
            onToggle={() => toggleSection('profitAndLoss')}
          >
            <LedgerTable
              rows={[
                { label: 'Total Commission', value: formatCurrency(ledgerData.profitAndLoss.totalCommission), align: 'right' },
                { label: 'Platform Compensation Credit', value: formatCurrency(ledgerData.profitAndLoss.totalInternalCompensation), align: 'right' },
                { label: 'Gateway Cost (Info)', value: formatCurrency(ledgerData.profitAndLoss.totalGatewayCharges), align: 'right' },
                { label: 'Net Profit', value: formatCurrency(ledgerData.profitAndLoss.netProfit), align: 'right', highlight: true },
              ]}
            />
          </LedgerSection>
        )}

        {showSection('settlementLedgers') && (
        <LedgerSection
          title="Settlement Entries"
          icon={<Receipt className="w-5 h-5" />}
          isExpanded={isGrievanceView || expandedSections.has('settlementLedgers')}
          onToggle={isGrievanceView ? undefined : () => toggleSection('settlementLedgers')}
        >
          {ledgerData.settlementLedgers?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Ledger ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Entry Source</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Lifecycle</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Gross</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Gateway Recovered</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Refund</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Internal Compensation</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Net Payable</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Settlement Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerData.settlementLedgers.map((row, idx) => (
                    (() => {
                      const vendorNetPayable =
                        row.netPayable < 0 && row.gatewayFeeRecovered > 0
                          ? row.netPayable + row.gatewayFeeRecovered
                          : row.netPayable;
                      return (
                    <tr key={`${row.ledgerId}-${idx}`} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-3 px-4 text-xs text-gray-700 dark:text-gray-300">{row.ledgerId}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{row.entrySource || 'N/A'}</td>
                      <td className="py-3 px-4">{getStatusBadge(row.lifecycleStatus || 'N/A')}</td>
                      <td className="py-3 px-4 text-right text-sm text-gray-900 dark:text-white">{formatCurrency(row.grossAmount)}</td>
                      <td className="py-3 px-4 text-right text-sm text-orange-600 dark:text-orange-400">{formatCurrency(row.gatewayFeeRecovered)}</td>
                      <td className="py-3 px-4 text-right text-sm text-red-600 dark:text-red-400">{formatCurrency(row.refundAmount)}</td>
                      <td className="py-3 px-4 text-right text-sm text-indigo-600 dark:text-indigo-400">{formatCurrency(row.internalCompensationAmount)}</td>
                      <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(vendorNetPayable)}</td>
                      <td className="py-3 px-4">{getStatusBadge(row.status)}</td>
                    </tr>
                      );
                    })()
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">No settlement ledger rows found</p>
          )}
        </LedgerSection>
        )}

        {showSection('gatewayCharges') && (
        <LedgerSection
          title="Gateway Charges"
          icon={<CreditCard className="w-5 h-5" />}
          isExpanded={isGrievanceView || expandedSections.has('gatewayCharges')}
          onToggle={isGrievanceView ? undefined : () => toggleSection('gatewayCharges')}
        >
          {ledgerData.gatewayCharges?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Charge ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Gateway</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerData.gatewayCharges.map((row, idx) => (
                    <tr key={`${row.gatewayChargeId}-${idx}`} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-3 px-4 text-xs text-gray-700 dark:text-gray-300">{row.gatewayChargeId}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{row.chargeType}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{row.gateway}</td>
                      <td className="py-3 px-4 text-right text-sm text-gray-900 dark:text-white">{formatCurrency(row.amount)}</td>
                      <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(row.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">No gateway charge rows found</p>
          )}
        </LedgerSection>
        )}

        {showSection('refunds') && (
        <LedgerSection
          title="Refunds"
          icon={<History className="w-5 h-5" />}
          isExpanded={isGrievanceView || expandedSections.has('refunds')}
          onToggle={isGrievanceView ? undefined : () => toggleSection('refunds')}
        >
          {ledgerData.refunds?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Refund ID</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Reason</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Initiated By</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerData.refunds.map((row, idx) => (
                    <tr key={`${row.refundId}-${idx}`} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-3 px-4 text-xs text-gray-700 dark:text-gray-300">{row.refundId}</td>
                      <td className="py-3 px-4 text-right text-sm text-gray-900 dark:text-white">{formatCurrency(row.amount)}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{row.reason || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{row.initiatedBy || 'N/A'}</td>
                      <td className="py-3 px-4">{getStatusBadge(row.status)}</td>
                      <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(row.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">No refund rows found</p>
          )}
        </LedgerSection>
        )}

        {showSection('payouts') && ledgerData.payouts.length > 0 && (
          <LedgerSection
            title="Payouts"
            icon={<Banknote className="w-5 h-5" />}
            isExpanded={expandedSections.has('payouts')}
            onToggle={() => toggleSection('payouts')}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Entity ID</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Method</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Reference</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerData.payouts.map((payout, index) => (
                    <tr key={index} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{payout.payoutType}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 text-sm">{payout.entityId.substring(0, 8)}...</td>
                      <td className="py-3 px-4 text-right font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(payout.amount)}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{payout.payoutMethod}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 text-sm">
                        {payout.referenceNumber || 'N/A'}
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(payout.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </LedgerSection>
        )}

        {showSection('auditTrail') && (
          <LedgerSection
            title="Audit Trail"
            icon={<History className="w-5 h-5" />}
            isExpanded={isGrievanceView || expandedSections.has('auditTrail')}
            onToggle={isGrievanceView ? undefined : () => toggleSection('auditTrail')}
          >
            {ledgerData.auditTrail.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">No audit trail entries found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Action</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Performed by</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Remarks</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerData.auditTrail.map((log, index) => (
                      <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">{log.actionType}</td>
                        <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">
                          {log.performedByName
                            ? `${log.performedByName}${log.performedByRole ? ` (${log.performedByRole})` : ''}`
                            : '\u2014'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{log.remarks || '\u2014'}</td>
                        <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {formatDate(log.performedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </LedgerSection>
        )}
      </div>
    </div>
  );
};

// Ledger Section Component
interface LedgerSectionProps {
  title: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}

const LedgerSection: React.FC<LedgerSectionProps> = ({
  title,
  icon,
  isExpanded,
  onToggle,
  children,
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        disabled={!onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors disabled:cursor-default disabled:hover:bg-transparent"
      >
        <div className="flex items-center gap-3">
          {icon}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        </div>
        {onToggle &&
          (isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ))}
      </button>
      {isExpanded && <div className="p-4 border-t border-gray-200 dark:border-gray-700">{children}</div>}
    </div>
  );
};

interface LedgerTableProps {
  rows: LedgerTableRow[];
  showHeader?: boolean;
}

const LedgerTable: React.FC<LedgerTableProps> = ({ rows, showHeader = false }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        {showHeader && (
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="py-3 px-4 text-left font-semibold text-gray-700 dark:text-gray-300">Field</th>
              <th className="py-3 px-4 text-right font-semibold text-gray-700 dark:text-gray-300">Value</th>
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={index}
              className={`border-b border-gray-100 dark:border-gray-800 ${
                row.highlight ? 'bg-primary-50 dark:bg-primary-900/20' : ''
              }`}
            >
              <td className="py-3 px-4 font-medium text-gray-700 dark:text-gray-300 w-1/2">
                {row.label}
              </td>
              <td
                className={`py-3 px-4 text-gray-900 dark:text-white ${
                  row.align === 'right' ? 'text-right font-semibold' : 'text-left'
                }`}
              >
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default OrderLedgerPage;
